import { useState, useEffect } from 'react';
import { Pill, Plus, X, Trash2, Sun, Sunrise, Sunset, Moon, Bell, Check, Edit3, AlertCircle } from 'lucide-react';
import { MED_TIMES } from '../lib/constants';
import { addMedication, updateMedication, deleteMedication } from '../lib/mock';
import { ensureNotificationsAllowed, scheduleMedicationReminders } from '../lib/notify';
import { subscribeToPush, isPushSupported, firePushTest } from '../lib/push';

const TIME_ICONS = { Sunrise, Sun, Sunset, Moon };

/**
 * MedicationsManager Modal — full CRUD for patient medications + browser reminders.
 * props: open, patient, onClose(updatedPatient), readOnly
 */
export default function MedicationsManager({ open, patient, onClose, readOnly = false }) {
  const [meds, setMeds] = useState([]);
  const [editingIndex, setEditingIndex] = useState(null); // null = list, -1 = new, N = edit
  const [form, setForm] = useState({ name: '', dose: '', schedule: [], notes: '' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [notifStatus, setNotifStatus] = useState('unknown'); // unknown | granted | denied | unsupported

  useEffect(() => {
    if (open && patient) {
      const raw = patient.medications || [];
      setMeds(raw.map(m => typeof m === 'string' ? { name: m, schedule: ['morning'] } : m));
      setEditingIndex(null);
      setError('');
      // Check notification support
      if (typeof Notification === 'undefined') setNotifStatus('unsupported');
      else if (Notification.permission === 'granted') setNotifStatus('granted');
      else if (Notification.permission === 'denied') setNotifStatus('denied');
      else setNotifStatus('unknown');
    }
  }, [open, patient]);

  const openNew = () => {
    setForm({ name: '', dose: '', schedule: [], notes: '' });
    setEditingIndex(-1);
    setError('');
  };

  const openEdit = (i) => {
    const m = meds[i] || {};
    setForm({ name: m.name || '', dose: m.dose || '', schedule: m.schedule || [], notes: m.notes || '' });
    setEditingIndex(i);
    setError('');
  };

  const toggleSchedule = (code) => {
    setForm(f => ({ ...f, schedule: f.schedule.includes(code) ? f.schedule.filter(c => c !== code) : [...f.schedule, code] }));
  };

  const save = async () => {
    if (!form.name.trim()) { setError('اسم الدواء مطلوب'); return; }
    if (form.schedule.length === 0) { setError('اختاري وقت واحد على الأقل للتذكير'); return; }
    setSaving(true); setError('');
    try {
      const payload = { name: form.name.trim(), dose: (form.dose || '').trim(), schedule: form.schedule, notes: (form.notes || '').trim() };
      let updated;
      if (editingIndex === -1) {
        updated = await addMedication(patient.id, payload);
      } else {
        updated = await updateMedication(patient.id, editingIndex, payload);
      }
      const newMeds = (updated.medications || []).map(m => typeof m === 'string' ? { name: m, schedule: ['morning'] } : m);
      setMeds(newMeds);
      // Reschedule reminders if permission granted
      if (notifStatus === 'granted') scheduleMedicationReminders(newMeds);
      setEditingIndex(null);
      if (onClose && typeof onClose === 'function') { /* keep modal open, but update parent */ }
      // Notify parent of update by calling onClose(updated) only on final close
      window.__lastPatientUpdate = updated;
    } catch (e) {
      setError(e.message || 'تعذّر الحفظ');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (i) => {
    if (!window.confirm('حذف هذا الدواء؟')) return;
    setSaving(true); setError('');
    try {
      const updated = await deleteMedication(patient.id, i);
      const newMeds = (updated.medications || []).map(m => typeof m === 'string' ? { name: m, schedule: ['morning'] } : m);
      setMeds(newMeds);
      if (notifStatus === 'granted') scheduleMedicationReminders(newMeds);
      window.__lastPatientUpdate = updated;
    } catch (e) {
      setError(e.message || 'تعذّر الحذف');
    } finally { setSaving(false); }
  };

  const enableReminders = async () => {
    const perm = await ensureNotificationsAllowed();
    setNotifStatus(perm);
    if (perm === 'granted') {
      scheduleMedicationReminders(meds);
      // Also subscribe to backend web-push so reminders fire when tab is closed
      if (isPushSupported()) {
        try {
          const res = await subscribeToPush(patient.id);
          if (res && res.ok) {
            new Notification('تم تفعيل التذكيرات ✅', { body: 'ستصلك تذكيرات لأدويتك حتى والتطبيق مغلق.', icon: '/favicon.ico' });
          } else {
            new Notification('تم تفعيل التذكيرات ✅', { body: 'التذكيرات ستعمل عندما التطبيق مفتوح.', icon: '/favicon.ico' });
          }
        } catch (e) {
          new Notification('تم تفعيل التذكيرات ✅', { body: 'التذكيرات ستعمل عندما التطبيق مفتوح.', icon: '/favicon.ico' });
        }
      } else {
        new Notification('تم تفعيل التذكيرات ✅', { body: 'ستصلك تذكيرات لأدويتك في مواعيدها.', icon: '/favicon.ico' });
      }
    }
  };

  const closeAll = () => {
    const updated = window.__lastPatientUpdate;
    window.__lastPatientUpdate = null;
    if (onClose) onClose(updated);
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50 backdrop-blur-sm animate-fade-in" data-testid="meds-modal">
      <div className="w-full sm:max-w-md max-h-[92vh] bg-white rounded-t-3xl sm:rounded-3xl overflow-hidden shadow-2xl animate-slide-up flex flex-col">
        {/* Header */}
        <div className="relative px-5 py-4" style={{ background: 'linear-gradient(135deg, #5eb2a3, #3f8579)' }}>
          <button onClick={closeAll} className="absolute top-3 left-3 w-8 h-8 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center backdrop-blur" data-testid="meds-close-btn">
            <X className="w-4 h-4 text-white" />
          </button>
          <div className="text-center">
            <div className="text-white/90 text-[11px] font-bold uppercase tracking-wider mb-1">إدارة أدويتك</div>
            <div className="text-white text-[16px] font-extrabold">جدولك الدوائي</div>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4">
          {editingIndex === null ? (
            <>
              {/* Reminders permission banner */}
              {notifStatus === 'unknown' && (
                <div className="mb-3 rounded-2xl p-3 flex items-center gap-2.5" style={{ background: 'var(--honey-bg)', border: '1px solid var(--honey)' }}>
                  <Bell className="w-4 h-4 flex-shrink-0" style={{ color: '#c9871f' }} />
                  <div className="flex-1 text-[11.5px] leading-relaxed" style={{ color: '#8a5f1a' }}>
                    <div className="font-extrabold mb-0.5">فعّلي التذكيرات</div>
                    <div>لتصلك إشعارات في مواعيد أدويتك</div>
                  </div>
                  <button onClick={enableReminders} className="rounded-xl px-3 py-1.5 text-[10.5px] font-bold text-white shadow-sm" style={{ background: 'linear-gradient(135deg, #f0b16c, #d99149)' }} data-testid="meds-enable-notif-btn">
                    تفعيل
                  </button>
                </div>
              )}
              {notifStatus === 'granted' && (
                <div className="mb-3 rounded-2xl p-2.5 flex items-center gap-2" style={{ background: 'var(--success-bg)', border: '1px solid var(--success)' }}>
                  <Check className="w-3.5 h-3.5" style={{ color: '#3b8f66' }} />
                  <div className="text-[11.5px] font-bold" style={{ color: '#3b8f66' }}>التذكيرات مفعّلة</div>
                </div>
              )}
              {notifStatus === 'denied' && (
                <div className="mb-3 rounded-2xl p-2.5 flex items-start gap-2" style={{ background: 'var(--danger-bg)', border: '1px solid rgba(229,133,133,0.35)' }}>
                  <AlertCircle className="w-3.5 h-3.5 mt-0.5" style={{ color: 'var(--danger)' }} />
                  <div className="text-[11px] leading-relaxed" style={{ color: '#a35555' }}>الإشعارات محظورة — فعّليها من إعدادات المتصفح</div>
                </div>
              )}

              {/* Meds list */}
              {meds.length === 0 ? (
                <div className="text-center py-8">
                  <div className="w-14 h-14 rounded-2xl mx-auto flex items-center justify-center mb-3" style={{ background: 'var(--surface-2)' }}>
                    <Pill className="w-6 h-6" style={{ color: 'var(--text-3)' }} />
                  </div>
                  <div className="text-[13px] font-extrabold mb-1" style={{ color: 'var(--text)' }}>لا توجد أدوية بعد</div>
                  <div className="text-[11px]" style={{ color: 'var(--text-2)' }}>أضيفي دواءك الأول لتبدأ التذكيرات</div>
                </div>
              ) : (
                <div className="space-y-2.5" data-testid="meds-list">
                  {meds.map((m, i) => {
                    const times = (m.schedule || []).map(c => MED_TIMES.find(t => t.code === c)).filter(Boolean);
                    return (
                      <div key={i} className="card-soft p-3" data-testid={`med-item-${i}`}>
                        <div className="flex items-start gap-3">
                          <div className="w-10 h-10 rounded-2xl flex items-center justify-center flex-shrink-0" style={{ background: 'var(--secondary-bg)' }}>
                            <Pill className="w-4.5 h-4.5" style={{ color: 'var(--secondary-dark)' }} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="text-[13px] font-extrabold" style={{ color: 'var(--text)' }}>{m.name}</div>
                            {m.dose && <div className="text-[10.5px] mt-0.5" style={{ color: 'var(--text-2)' }}>{m.dose}</div>}
                            <div className="flex flex-wrap gap-1 mt-1.5">
                              {times.map(t => {
                                const Icon = TIME_ICONS[t.icon] || Sun;
                                return (
                                  <div key={t.code} className="flex items-center gap-1 rounded-full px-2 py-0.5 text-[9.5px] font-bold" style={{ background: `${t.color}22`, color: t.color }}>
                                    <Icon className="w-2.5 h-2.5" />
                                    {t.label} · {t.hour}
                                  </div>
                                );
                              })}
                            </div>
                            {m.notes && <div className="text-[10.5px] mt-2 italic" style={{ color: 'var(--text-3)' }}>{m.notes}</div>}
                          </div>
                          {!readOnly && (
                            <div className="flex flex-col gap-1 flex-shrink-0">
                              <button onClick={() => openEdit(i)} className="w-8 h-8 rounded-xl flex items-center justify-center bg-[var(--surface-2)] hover:bg-[var(--border-soft)]" data-testid={`med-edit-${i}`}>
                                <Edit3 className="w-3.5 h-3.5" style={{ color: 'var(--text-2)' }} />
                              </button>
                              <button onClick={() => remove(i)} className="w-8 h-8 rounded-xl flex items-center justify-center hover:bg-[var(--danger-bg)]" data-testid={`med-delete-${i}`}>
                                <Trash2 className="w-3.5 h-3.5" style={{ color: 'var(--danger)' }} />
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {!readOnly && (
                <button onClick={openNew} className="w-full mt-4 py-3.5 rounded-2xl text-white font-extrabold flex items-center justify-center gap-2 shadow-md active:scale-[0.98] transition-transform" style={{ background: 'linear-gradient(135deg, #5eb2a3, #3f8579)' }} data-testid="meds-add-btn">
                  <Plus className="w-4 h-4" />
                  أضيفي دواءً جديداً
                </button>
              )}
            </>
          ) : (
            /* Edit / Add form */
            <div className="space-y-3.5" data-testid="med-form">
              <div>
                <label className="text-[11px] font-extrabold block mb-1.5" style={{ color: 'var(--text-2)' }}>اسم الدواء</label>
                <input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="مثلاً: ليثيوم" className="w-full bg-[var(--surface-2)] border-2 border-[var(--border-soft)] focus:border-[var(--secondary)] rounded-2xl px-4 py-3 text-[13.5px] outline-none transition" data-testid="med-form-name" />
              </div>
              <div>
                <label className="text-[11px] font-extrabold block mb-1.5" style={{ color: 'var(--text-2)' }}>الجرعة</label>
                <input value={form.dose} onChange={e => setForm({ ...form, dose: e.target.value })} placeholder="مثلاً: 400mg — قرص واحد" className="w-full bg-[var(--surface-2)] border-2 border-[var(--border-soft)] focus:border-[var(--secondary)] rounded-2xl px-4 py-3 text-[13.5px] outline-none transition" data-testid="med-form-dose" />
              </div>
              <div>
                <label className="text-[11px] font-extrabold block mb-2" style={{ color: 'var(--text-2)' }}>مواعيد التذكير</label>
                <div className="grid grid-cols-2 gap-2">
                  {MED_TIMES.map(t => {
                    const Icon = TIME_ICONS[t.icon] || Sun;
                    const on = form.schedule.includes(t.code);
                    return (
                      <button key={t.code} onClick={() => toggleSchedule(t.code)} className={`flex items-center gap-2 rounded-2xl p-2.5 border-2 transition-all ${on ? 'shadow-sm' : ''}`}
                        style={on ? { background: `${t.color}18`, borderColor: t.color } : { background: 'var(--surface-2)', borderColor: 'var(--border-soft)' }}
                        data-testid={`med-form-time-${t.code}`}
                      >
                        <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ background: `${t.color}22` }}>
                          <Icon className="w-3.5 h-3.5" style={{ color: t.color }} />
                        </div>
                        <div className="text-right flex-1">
                          <div className="text-[11.5px] font-extrabold" style={{ color: 'var(--text)' }}>{t.label}</div>
                          <div className="text-[9.5px]" style={{ color: 'var(--text-2)' }}>{t.hour}</div>
                        </div>
                        <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center flex-shrink-0`} style={on ? { background: t.color, borderColor: t.color } : { borderColor: 'var(--text-3)' }}>
                          {on && <Check className="w-2.5 h-2.5 text-white" strokeWidth={3} />}
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
              <div>
                <label className="text-[11px] font-extrabold block mb-1.5" style={{ color: 'var(--text-2)' }}>ملاحظات (اختياري)</label>
                <textarea value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} rows={2} placeholder="مثلاً: مع الطعام، تجنّب الكافيين…" className="w-full bg-[var(--surface-2)] border-2 border-[var(--border-soft)] focus:border-[var(--secondary)] rounded-2xl px-4 py-3 text-[13px] outline-none resize-none transition" data-testid="med-form-notes"></textarea>
              </div>

              {error && (
                <div className="rounded-2xl px-3 py-2.5 text-[12px] font-semibold flex items-center gap-2" style={{ background: 'var(--danger-bg)', color: '#a35555' }}>
                  <AlertCircle className="w-3.5 h-3.5" />
                  {error}
                </div>
              )}

              <div className="flex gap-2 pt-2">
                <button onClick={() => setEditingIndex(null)} className="flex-1 py-3 rounded-2xl text-[12.5px] font-extrabold border-2" style={{ borderColor: 'var(--border)', color: 'var(--text-2)' }} data-testid="med-form-cancel">
                  إلغاء
                </button>
                <button onClick={save} disabled={saving} className="flex-1 py-3 rounded-2xl text-white font-extrabold text-[12.5px] shadow-md disabled:opacity-50" style={{ background: 'linear-gradient(135deg, #5eb2a3, #3f8579)' }} data-testid="med-form-save">
                  {saving ? 'يحفظ…' : (editingIndex === -1 ? 'أضيفي' : 'حفظ')}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
